import React, { useEffect, useMemo, useRef, useState } from "react";


function clamp(x, a, b) {
  return Math.min(b, Math.max(a, x));
}

function mulberry32(seed) {
  // deterministischer Zufall (reproduzierbar)
  let t = seed >>> 0;
  return function () {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(rng, arr) {
  return arr[Math.floor(rng() * arr.length)];
}

function fmtTime(ts) {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function id(prefix = "e") {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function within(events, nowTs, windowMs) {
  const minTs = nowTs - windowMs;
  return events.filter((e) => e.ts >= minTs);
}

/* ----------------------------- Data ----------------------------------- */

// Test-Net IPs (nicht echt, gut für Demos)
const IPS = {
  de: ["203.0.113.10", "203.0.113.22", "203.0.113.35", "203.0.113.41"],
  us: ["198.51.100.10", "198.51.100.23", "198.51.100.44", "198.51.100.88"],
  other: ["192.0.2.12", "192.0.2.55", "192.0.2.77", "192.0.2.101"],
};

const GEO = {
  de: "DE",
  us: "US",
  other: "OTHER",
};

// Nur 2 Szenarien: Shop + Bank
const SCENARIOS = [
  {
    key: "webshop",
    name: "Online‑Shop",
    sources: ["auth", "web", "api", "db", "waf"],
    users: ["alice", "bob", "carla", "shopadmin"],
  },
  {
    key: "bank",
    name: "Online‑Bankplattform",
    sources: ["auth", "bank", "payments", "core", "fraud", "api", "waf"],
    users: ["cust_anna", "cust_ben", "cust_carla", "bank_admin"],
  },
];


const DEFAULT_RULES = {
  credentialStuffing: true,
  impossibleTravel: true,
  beneficiaryThenTransfer: true, // nur Bank sinnvoll
  privilegeChain: true,
  massDataAccess: true,
  dosSpike: true,
};

function ruleDefs(scenarioKey) {
  const defs = [
    {
      key: "credentialStuffing",
      name: "Viele Login‑Fehler + später Erfolg",
      severity: "hoch",
      explain:
        "Viele Fehlversuche innerhalb kurzer Zeit und danach ein erfolgreicher Login ist oft ein Warnsignal.",
    },
    {
      key: "impossibleTravel",
      name: "Ungewöhnlicher Ortswechsel bei Logins",
      severity: "mittel",
      explain:
        "Ein Nutzer loggt sich kurz hintereinander aus sehr unterschiedlichen Regionen ein → kann Konto‑Missbrauch sein.",
    },
    {
      key: "privilegeChain",
      name: "Admin‑Login + kurz danach sensible Aktion",
      severity: "hoch",
      explain:
        "Wenn ein Admin‑Login im Zeitraum auftaucht und kurz danach sensible Aktionen passieren, lohnt sich ein genauer Blick.",
    },
    {
      key: "massDataAccess",
      name: "Viele Downloads/Exports in kurzer Zeit",
      severity: "hoch",
      explain:
        "Viele Datenzugriffe in kurzer Zeit können Datenabfluss sein oder Automatisierung (nicht immer Angriff).",
    },
    {
      key: "dosSpike",
      name: "Viele Requests/Blocks von einer IP",
      severity: "mittel",
      explain:
        "Sehr viele Requests/Blocks von einer IP in kurzer Zeit können Bots/DoS oder Fehlkonfiguration sein.",
    },
  ];

  if (scenarioKey === "bank") {
    defs.splice(2, 0, {
      key: "beneficiaryThenTransfer",
      name: "Neuer Empfänger + Überweisung kurz danach",
      severity: "hoch",
      explain:
        "Häufiges Fraud‑Muster: neuer Empfänger wird angelegt und kurz danach wird eine Überweisung bestätigt.",
    });
  }

  return defs;
}

// Cooldown: gleiche Regel für gleiche Entity nicht dauernd spammen
const ALERT_COOLDOWN_MS = 2 * 60 * 1000;

/* ----------------------------- Event Generation ------------------------ */

function makeRawLine(e) {
  const base = `${fmtTime(e.ts)} src=${e.source} type=${e.type}`;
  const u = e.user ? ` user=${e.user}` : "";
  const ip = e.ip ? ` ip=${e.ip}` : "";
  const geo = e.geo ? ` geo=${e.geo}` : "";
  const out = e.outcome ? ` outcome=${e.outcome}` : "";
  const msg = e.msg ? ` msg="${e.msg}"` : "";
  return base + u + ip + geo + out + msg;
}

function mkEvent({ nowTs, source, type, user, ip, geo, outcome, msg, meta }) {
  const e = {
    id: id("evt"),
    ts: nowTs,
    source,
    type,
    user,
    ip,
    geo,
    outcome,
    msg,
    meta: meta || {},
  };
  e.raw = makeRawLine(e);
  return e;
}

function baseGeoGermany(rng) {
  // Normale Events: immer DE
  const geoKey = "de";
  return { ip: pick(rng, IPS[geoKey]), geo: GEO[geoKey] };
}

function genNormalEvent({ scenarioKey, rng, nowTs }) {
  const s = SCENARIOS.find((x) => x.key === scenarioKey) || SCENARIOS[0];
  const user = pick(rng, s.users);

  // Normale Geo‑Logs: DE
  const { ip, geo } = baseGeoGermany(rng);

  /* ------------------- Online Shop ---------------------------------- */
  if (scenarioKey === "webshop") {
    const roll = rng();

    if (roll < 0.18) {
      const ok = rng() < 0.88;
      return mkEvent({
        nowTs,
        source: "auth",
        type: ok ? "LOGIN_SUCCESS" : "LOGIN_FAIL",
        user,
        ip,
        geo,
        outcome: ok ? "success" : "fail",
        msg: ok ? "Login erfolgreich" : "Login fehlgeschlagen",
      });
    }

    if (roll < 0.36) {
      return mkEvent({
        nowTs,
        source: "web",
        type: "VIEW_PRODUCT",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Produktseite aufgerufen",
      });
    }

    if (roll < 0.52) {
      return mkEvent({
        nowTs,
        source: "web",
        type: "ADD_TO_CART",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Artikel in Warenkorb gelegt",
      });
    }

    if (roll < 0.62) {
      return mkEvent({
        nowTs,
        source: "web",
        type: "CHECKOUT_START",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Checkout gestartet",
      });
    }

    if (roll < 0.70) {
      return mkEvent({
        nowTs,
        source: "api",
        type: "API_REQUEST",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "API Request",
        meta: { count: 1 + Math.floor(rng() * 8) },
      });
    }

    if (roll < 0.78) {
      return mkEvent({
        nowTs,
        source: "auth",
        type: "PASSWORD_RESET_REQUEST",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Passwort‑Reset angefordert",
      });
    }

    if (roll < 0.85) {
      const isAdmin = user === "shopadmin";
      if (isAdmin && rng() < 0.35) {
        return mkEvent({
          nowTs,
          source: "db",
          type: "ADMIN_PRICE_CHANGE",
          user,
          ip,
          geo,
          outcome: "ok",
          msg: "Preisänderung durchgeführt",
        });
      }
      return mkEvent({
        nowTs,
        source: "web",
        type: "SEARCH",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Suche ausgeführt",
      });
    }

    // WAF blockt manchmal Bots
    return mkEvent({
      nowTs,
      source: "waf",
      type: "WAF_BLOCK",
      user: null,
      ip,
      geo,
      outcome: "blocked",
      msg: "WAF blockiert verdächtige Anfrage",
      meta: { count: 1 + Math.floor(rng() * 20) },
    });
  }

  /* ------------------- Online Bank ---------------------------------- */
  {
    const roll = rng();

    if (roll < 0.20) {
      const ok = rng() < 0.90;
      return mkEvent({
        nowTs,
        source: "auth",
        type: ok ? "LOGIN_SUCCESS" : "LOGIN_FAIL",
        user,
        ip,
        geo,
        outcome: ok ? "success" : "fail",
        msg: ok ? "Login erfolgreich" : "Login fehlgeschlagen",
      });
    }

    if (roll < 0.30) {
      const ok = rng() < 0.92;
      return mkEvent({
        nowTs,
        source: "auth",
        type: ok ? "MFA_OK" : "MFA_FAIL",
        user,
        ip,
        geo,
        outcome: ok ? "success" : "fail",
        msg: ok ? "MFA bestätigt" : "MFA fehlgeschlagen",
      });
    }

    if (roll < 0.46) {
      return mkEvent({
        nowTs,
        source: "bank",
        type: "VIEW_BALANCE",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Kontostand angesehen",
      });
    }

    if (roll < 0.60) {
      return mkEvent({
        nowTs,
        source: "bank",
        type: "VIEW_TRANSACTIONS",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Umsätze angesehen",
      });
    }

    if (roll < 0.70) {
      return mkEvent({
        nowTs,
        source: "bank",
        type: "DOWNLOAD_STATEMENT",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Kontoauszug heruntergeladen",
        meta: { count: 1 + Math.floor(rng() * 2) },
      });
    }

    if (roll < 0.78) {
      const field = pick(rng, ["email", "phone", "address"]);
      return mkEvent({
        nowTs,
        source: "core",
        type: "PROFILE_CHANGE",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: `Profildaten geändert (${field})`,
      });
    }

    if (roll < 0.84) {
      return mkEvent({
        nowTs,
        source: "payments",
        type: "ADD_BENEFICIARY",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Neuer Empfänger hinzugefügt",
      });
    }

    if (roll < 0.90) {
      return mkEvent({
        nowTs,
        source: "payments",
        type: "TRANSFER_INIT",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Überweisung gestartet",
      });
    }

    if (roll < 0.94) {
      return mkEvent({
        nowTs,
        source: "payments",
        type: "TRANSFER_CONFIRM",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Überweisung bestätigt",
      });
    }

    if (roll < 0.98) {
      return mkEvent({
        nowTs,
        source: "api",
        type: "API_REQUEST",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "API Request",
        meta: { count: 1 + Math.floor(rng() * 8) },
      });
    }

    return mkEvent({
      nowTs,
      source: "waf",
      type: "WAF_BLOCK",
      user: null,
      ip,
      geo,
      outcome: "blocked",
      msg: "WAF blockiert verdächtige Anfrage",
      meta: { count: 1 + Math.floor(rng() * 22) },
    });
  }
}

/* ----------------------------- Pattern Injection ----------------------- */

function injectPattern({ scenarioKey, nowTs, benign = false }) {
  // Nur Muster/Detektion – keine Exploit‑Details.
  if (scenarioKey === "webshop") {
    // Verdächtig: viele Login‑Fails -> Erfolg -> Export‑Burst (aus US)
    const ip = "198.51.100.88";
    const geo = "US";
    const user = "alice";
    const seq = [];

    for (let i = 0; i < 7; i++) {
      seq.push(
        mkEvent({
          nowTs: nowTs + i * 6000,
          source: "auth",
          type: "LOGIN_FAIL",
          user,
          ip,
          geo,
          outcome: "fail",
          msg: "Login fehlgeschlagen (mehrfach)",
        })
      );
    }

    seq.push(
      mkEvent({
        nowTs: nowTs + 7 * 6000,
        source: "auth",
        type: "LOGIN_SUCCESS",
        user,
        ip,
        geo,
        outcome: "success",
        msg: "Login erfolgreich (nach vielen Fehlern)",
      })
    );

    for (let i = 0; i < 18; i++) {
      seq.push(
        mkEvent({
          nowTs: nowTs + 8 * 6000 + i * 1500,
          source: "db",
          type: "EXPORT_CUSTOMERS",
          user,
          ip,
          geo,
          outcome: "ok",
          msg: "Kundendaten exportiert (ungewöhnlich häufig)",
          meta: { count: 1 },
        })
      );
    }
    return seq;
  }

  // Bank
  {
    const user = "cust_anna";

    if (benign) {
      // Legitimer Fall (False Positive Demo): DE‑Login + neuer Empfänger + Überweisung
      // -> Regel kann trotzdem anschlagen, weil das Muster ähnlich ist.
      const ip = "203.0.113.22";
      const geo = "DE";
      const seq = [];

      seq.push(
        mkEvent({
          nowTs,
          source: "auth",
          type: "LOGIN_SUCCESS",
          user,
          ip,
          geo,
          outcome: "success",
          msg: "Login erfolgreich",
          meta: { demo: "benign" },
        })
      );

      seq.push(
        mkEvent({
          nowTs: nowTs + 8000,
          source: "auth",
          type: "MFA_OK",
          user,
          ip,
          geo,
          outcome: "success",
          msg: "MFA bestätigt",
          meta: { demo: "benign" },
        })
      );

      // Kontext‑Event: macht es „realistischer“, dass es legitim sein könnte
      seq.push(
        mkEvent({
          nowTs: nowTs + 18000,
          source: "fraud",
          type: "CUSTOMER_CONTEXT",
          user,
          ip,
          geo,
          outcome: "ok",
          msg: "Kunde kündigt neuen Empfänger an (z. B. Umzug / neuer Vermieter)",
          meta: { demo: "benign" },
        })
      );

      seq.push(
        mkEvent({
          nowTs: nowTs + 26000,
          source: "payments",
          type: "ADD_BENEFICIARY",
          user,
          ip,
          geo,
          outcome: "ok",
          msg: "Neuer Empfänger hinzugefügt",
          meta: { demo: "benign" },
        })
      );

      seq.push(
        mkEvent({
          nowTs: nowTs + 42000,
          source: "payments",
          type: "TRANSFER_INIT",
          user,
          ip,
          geo,
          outcome: "ok",
          msg: "Überweisung gestartet",
          meta: { demo: "benign" },
        })
      );

      seq.push(
        mkEvent({
          nowTs: nowTs + 56000,
          source: "payments",
          type: "TRANSFER_CONFIRM",
          user,
          ip,
          geo,
          outcome: "ok",
          msg: "Überweisung bestätigt (kurz nach neuem Empfänger)",
          meta: { demo: "benign" },
        })
      );

      return seq;
    }

    // Verdächtig: viele Login‑Fails -> Erfolg (US) -> Empfänger -> Transfer
    const ip = "198.51.100.23";
    const geo = "US";
    const seq = [];

    for (let i = 0; i < 6; i++) {
      seq.push(
        mkEvent({
          nowTs: nowTs + i * 7000,
          source: "auth",
          type: "LOGIN_FAIL",
          user,
          ip,
          geo,
          outcome: "fail",
          msg: "Login fehlgeschlagen (mehrfach)",
          meta: { demo: "fraud" },
        })
      );
    }

    seq.push(
      mkEvent({
        nowTs: nowTs + 6 * 7000,
        source: "auth",
        type: "LOGIN_SUCCESS",
        user,
        ip,
        geo,
        outcome: "success",
        msg: "Login erfolgreich (nach vielen Fehlern)",
        meta: { demo: "fraud" },
      })
    );

    seq.push(
      mkEvent({
        nowTs: nowTs + 6 * 7000 + 10000,
        source: "core",
        type: "PROFILE_CHANGE",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Profildaten geändert (z. B. Kontakt)",
        meta: { demo: "fraud" },
      })
    );

    seq.push(
      mkEvent({
        nowTs: nowTs + 6 * 7000 + 22000,
        source: "payments",
        type: "ADD_BENEFICIARY",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Neuer Empfänger hinzugefügt (ungewöhnlich)",
        meta: { demo: "fraud" },
      })
    );

    seq.push(
      mkEvent({
        nowTs: nowTs + 6 * 7000 + 34000,
        source: "payments",
        type: "TRANSFER_INIT",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Überweisung gestartet",
        meta: { demo: "fraud" },
      })
    );

    seq.push(
      mkEvent({
        nowTs: nowTs + 6 * 7000 + 46000,
        source: "payments",
        type: "TRANSFER_CONFIRM",
        user,
        ip,
        geo,
        outcome: "ok",
        msg: "Überweisung bestätigt (kurz nach neuem Empfänger)",
        meta: { demo: "fraud" },
      })
    );

    return seq;
  }
}

/* ----------------------------- Correlation Engine ---------------------- */

function makeAlert({ ruleKey, ruleName, severity, ts, title, entityKey, details }) {
  return {
    id: id("al"),
    ts,
    severity, // "hoch" | "mittel"
    ruleKey,
    ruleName,
    title,
    entityKey,
    details,
  };
}

function runCorrelation({ newEvent, allEvents, scenarioKey, rulesEnabled, dedupeRef }) {
  const alerts = [];
  const nowTs = newEvent.ts;

  const canFire = (ruleKey, entityKey) => {
    const key = `${ruleKey}::${entityKey}`;
    const last = dedupeRef.current.get(key);
    if (!last) return true;
    return nowTs - last >= ALERT_COOLDOWN_MS;
  };

  const markFired = (ruleKey, entityKey) => {
    const key = `${ruleKey}::${entityKey}`;
    dedupeRef.current.set(key, nowTs);
  };

  /* Rule: Credential Stuffing */
  if (rulesEnabled.credentialStuffing) {
    if (newEvent.type === "LOGIN_SUCCESS" && newEvent.user && newEvent.ip) {
      const recent = within(allEvents, nowTs, 3 * 60 * 1000);
      const fails = recent.filter(
        (e) =>
          e.type === "LOGIN_FAIL" &&
          e.user === newEvent.user &&
          e.ip === newEvent.ip &&
          e.outcome === "fail"
      );

      if (fails.length >= 5) {
        const entityKey = `${newEvent.user}@${newEvent.ip}`;
        if (canFire("credentialStuffing", entityKey)) {
          alerts.push(
            makeAlert({
              ruleKey: "credentialStuffing",
              ruleName: "Viele Login‑Fehler + später Erfolg",
              severity: "hoch",
              ts: nowTs,
              title: `Verdächtiges Login‑Muster bei ${newEvent.user}`,
              entityKey,
              details: `In den letzten Minuten gab es ${fails.length} Fehlversuche von derselben IP, danach einen erfolgreichen Login.`,
            })
          );
          markFired("credentialStuffing", entityKey);
        }
      }
    }
  }

  /* Rule: Impossible Travel */
  if (rulesEnabled.impossibleTravel) {
    if (newEvent.type === "LOGIN_SUCCESS" && newEvent.user && newEvent.geo) {
      const recent = within(allEvents, nowTs, 10 * 60 * 1000);
      const logins = recent
        .filter((e) => e.type === "LOGIN_SUCCESS" && e.user === newEvent.user && e.geo)
        .sort((a, b) => a.ts - b.ts);

      if (logins.length >= 2) {
        const [a, b] = logins.slice(-2);
        if (a.geo !== b.geo) {
          const entityKey = `${newEvent.user}`;
          if (canFire("impossibleTravel", entityKey)) {
            alerts.push(
              makeAlert({
                ruleKey: "impossibleTravel",
                ruleName: "Ungewöhnlicher Ortswechsel bei Logins",
                severity: "mittel",
                ts: nowTs,
                title: `Ungewöhnliche Login‑Orte für ${newEvent.user}`,
                entityKey,
                details: `Zwei erfolgreiche Logins innerhalb kurzer Zeit aus ${a.geo} und ${b.geo}.`,
              })
            );
            markFired("impossibleTravel", entityKey);
          }
        }
      }
    }
  }

  /* Rule: New beneficiary + transfer shortly after (nur Bank) */
  if (scenarioKey === "bank" && rulesEnabled.beneficiaryThenTransfer) {
    if (newEvent.type === "TRANSFER_CONFIRM" && newEvent.user) {
      const recent = within(allEvents, nowTs, 15 * 60 * 1000);
      const hasBeneficiary = recent.some(
        (e) => e.type === "ADD_BENEFICIARY" && e.user === newEvent.user
      );

      if (hasBeneficiary) {
        const entityKey = `${newEvent.user}`;
        if (canFire("beneficiaryThenTransfer", entityKey)) {
          const hasContext = recent.some(
            (e) =>
              e.user === newEvent.user &&
              (e.type === "CUSTOMER_CONTEXT" || e.meta?.demo === "benign")
          );

          const severity = hasContext ? "mittel" : "hoch";
          const title = hasContext
            ? `Überweisung nach neuem Empfänger (Review) – ${newEvent.user}`
            : `Überweisung kurz nach neuem Empfänger – ${newEvent.user}`;

          const details = hasContext
            ? "Muster erkannt (Empfänger + Überweisung). Zusätzlich gibt es Kontext, der auf einen legitimen Fall hindeuten kann → trotzdem prüfen."
            : "Muster erkannt (Empfänger + Überweisung). Das ist ein starkes Fraud‑Signal, sollte geprüft werden.";

          alerts.push(
            makeAlert({
              ruleKey: "beneficiaryThenTransfer",
              ruleName: "Neuer Empfänger + Überweisung kurz danach",
              severity,
              ts: nowTs,
              title,
              entityKey,
              details,
            })
          );
          markFired("beneficiaryThenTransfer", entityKey);
        }
      }
    }
  }

  /* Rule: Admin login in window + sensitive action */
  if (rulesEnabled.privilegeChain) {
    const sensitive =
      scenarioKey === "webshop"
        ? ["ADMIN_PRICE_CHANGE", "EXPORT_CUSTOMERS"]
        : ["PROFILE_CHANGE", "ADD_BENEFICIARY", "TRANSFER_INIT", "TRANSFER_CONFIRM"];

    if (sensitive.includes(newEvent.type) && newEvent.user) {
      const recent = within(allEvents, nowTs, 12 * 60 * 1000);

      const adminLogin = recent.find(
        (e) => e.type === "LOGIN_SUCCESS" && e.user && e.user.includes("admin")
      );

      if (adminLogin) {
        const entityKey = `${newEvent.user}`;
        if (canFire("privilegeChain", entityKey)) {
          alerts.push(
            makeAlert({
              ruleKey: "privilegeChain",
              ruleName: "Admin‑Login + sensible Aktion",
              severity: "hoch",
              ts: nowTs,
              title: `Sensible Aktion im Zeitraum eines Admin‑Logins`,
              entityKey,
              details: `Im Zeitraum gab es einen Admin‑Login (${adminLogin.user}). Danach: ${newEvent.type} durch ${newEvent.user}.`,
            })
          );
          markFired("privilegeChain", entityKey);
        }
      }
    }
  }

  /* Rule: Mass data access */
  if (rulesEnabled.massDataAccess) {
    const massTypes =
      scenarioKey === "webshop" ? ["EXPORT_CUSTOMERS"] : ["DOWNLOAD_STATEMENT"];

    if (newEvent.user && massTypes.includes(newEvent.type)) {
      const recent = within(allEvents, nowTs, 5 * 60 * 1000);
      const count = recent.filter(
        (e) => e.user === newEvent.user && massTypes.includes(e.type)
      ).length;

      const threshold = scenarioKey === "bank" ? 10 : 18;

      if (count >= threshold) {
        const entityKey = `${newEvent.user}`;
        if (canFire("massDataAccess", entityKey)) {
          alerts.push(
            makeAlert({
              ruleKey: "massDataAccess",
              ruleName: "Viele Downloads/Exports in kurzer Zeit",
              severity: "hoch",
              ts: nowTs,
              title: `Viele Datenzugriffe durch ${newEvent.user}`,
              entityKey,
              details: `In den letzten 5 Minuten wurden ${count} relevante Aktionen gezählt.`,
            })
          );
          markFired("massDataAccess", entityKey);
        }
      }
    }
  }

  /* Rule: DoS / Traffic spike by IP */
  if (rulesEnabled.dosSpike) {
    if (newEvent.ip && (newEvent.type === "API_REQUEST" || newEvent.type === "WAF_BLOCK")) {
      const recent = within(allEvents, nowTs, 60 * 1000);
      const ipEvents = recent.filter(
        (e) =>
          e.ip === newEvent.ip &&
          (e.type === "API_REQUEST" || e.type === "WAF_BLOCK")
      );

      const score = ipEvents.reduce((acc, e) => acc + (e.meta?.count || 1), 0);

      if (score >= 80) {
        const entityKey = `${newEvent.ip}`;
        if (canFire("dosSpike", entityKey)) {
          alerts.push(
            makeAlert({
              ruleKey: "dosSpike",
              ruleName: "Viele Requests/Blocks von einer IP",
              severity: "mittel",
              ts: nowTs,
              title: `Traffic‑Spike von IP ${newEvent.ip}`,
              entityKey,
              details: `In ~1 Minute wurden (gewichtet) ca. ${score} Requests/Blocks gezählt.`,
            })
          );
          markFired("dosSpike", entityKey);
        }
      }
    }
  }

  return alerts;
}

/* ----------------------------- Fraud Score (Bank) ---------------------- */

function computeFraudScoresForBank(events, nowTs) {
  const w15 = within(events, nowTs, 15 * 60 * 1000);
  const users = Array.from(
    new Set(w15.map((e) => e.user).filter(Boolean).filter((u) => u.startsWith("cust_")))
  );

  const scores = users.map((user) => {
    let score = 0;
    const reasons = [];

    // Login‑Fails (3 Minuten)
    const w3 = within(events, nowTs, 3 * 60 * 1000).filter(
      (e) => e.user === user && e.type === "LOGIN_FAIL"
    );
    if (w3.length >= 5) {
      score += 35;
      reasons.push(`Viele Login‑Fehler (${w3.length})`);
    } else if (w3.length >= 3) {
      score += 15;
      reasons.push(`Mehrere Login‑Fehler (${w3.length})`);
    }

    // Impossible travel (10 Minuten) – braucht unterschiedliche Geos (kommt i. d. R. über Muster)
    const w10 = within(events, nowTs, 10 * 60 * 1000)
      .filter((e) => e.user === user && e.type === "LOGIN_SUCCESS" && e.geo)
      .sort((a, b) => a.ts - b.ts);
    const geos = Array.from(new Set(w10.map((e) => e.geo)));
    if (geos.length >= 2) {
      score += 25;
      reasons.push(`Ungewöhnliche Orte (${geos.join("→")})`);
    }

    // Neuer Empfänger + Transfer Confirm (15 Minuten)
    const hasBen = w15.some((e) => e.user === user && e.type === "ADD_BENEFICIARY");
    const hasTx = w15.some((e) => e.user === user && e.type === "TRANSFER_CONFIRM");
    if (hasBen && hasTx) {
      score += 30;
      reasons.push("Empfänger + Überweisung");
    }

    // Profiländerung (15 Minuten)
    const hasProfile = w15.some((e) => e.user === user && e.type === "PROFILE_CHANGE");
    if (hasProfile) {
      score += 15;
      reasons.push("Profiländerung");
    }

    // Viele Kontoauszüge (5 Minuten)
    const w5 = within(events, nowTs, 5 * 60 * 1000).filter(
      (e) => e.user === user && e.type === "DOWNLOAD_STATEMENT"
    );
    if (w5.length >= 8) {
      score += 20;
      reasons.push(`Viele Auszüge (${w5.length})`);
    }

    // Kontext (False Positive Demo): reduziert Score, zeigt Priorisierung
    const hasContext = w15.some(
      (e) => e.user === user && (e.type === "CUSTOMER_CONTEXT" || e.meta?.demo === "benign")
    );
    if (hasContext) {
      score -= 20;
      reasons.push("Kontext vorhanden (könnte legitim sein)");
    }

    score = clamp(score, 0, 100);

    return { user, score, reasons };
  });

  scores.sort((a, b) => b.score - a.score);
  return scores.slice(0, 4);
}

/* ----------------------------- UI ------------------------------------- */

const css = `
.msiem { margin: 0; max-width: 1200px; }
.msiemCard { border: 1px solid rgba(0,0,0,.12); border-radius: 14px; background: rgba(255,255,255,.92); padding: 14px; }
.msiemHeader { display:flex; gap: 12px; align-items:flex-start; justify-content: space-between; flex-wrap: wrap; }
.msiemTitle { margin: 0; font-size: 1.15rem; }
.msiemSub { margin-top: 6px; opacity: .82; font-size: .95rem; max-width: 85ch; }
.msiemRow { display:flex; gap: 10px; align-items:center; flex-wrap: wrap; }

.msiemSelect, .msiemInput { padding: 8px 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.16); background: rgba(255,255,255,.95); }
.msiemBtn { padding: 8px 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.16); background: rgba(255,255,255,.95); cursor:pointer; }
.msiemBtnPrimary { background: rgba(0,0,0,.06); font-weight: 800; }
.msiemTag { display:inline-block; padding: 3px 8px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12); background: rgba(0,0,0,.03); font-size: .82rem; }

.msiemGrid { display:grid; grid-template-columns: 1.2fr .8fr; gap: 14px; margin-top: 12px; }
@media (max-width: 1000px){ .msiemGrid { grid-template-columns: 1fr; } }

.msiemPanel { border: 1px solid rgba(0,0,0,.12); border-radius: 14px; padding: 12px; background: rgba(0,0,0,.02); }
.msiemPanelTitle { font-weight: 900; margin-bottom: 8px; }

.msiemRules { display:grid; gap: 8px; }
.msiemRule { display:flex; align-items:flex-start; justify-content: space-between; gap: 10px; padding: 10px; border-radius: 12px; border: 1px solid rgba(0,0,0,.12); background: rgba(255,255,255,.9); }
.msiemRuleLeft { display:flex; flex-direction: column; gap: 2px; }
.msiemRuleName { font-weight: 900; }
.msiemRuleHelp { font-size: .88rem; opacity: .78; }
.msiemRuleToggle { display:flex; flex-direction: column; gap: 6px; align-items:flex-end; }
.msiemSmall { font-size: .82rem; opacity: .7; }

.msiemStats { display:grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-top: 10px; }
.msiemStat { border: 1px solid rgba(0,0,0,.12); border-radius: 12px; background: rgba(255,255,255,.9); padding: 10px; }
.msiemStatTitle { font-size: .9rem; opacity: .8; }
.msiemStatValue { margin-top: 4px; font-size: 1.15rem; font-weight: 900; }

.msiemSplit { display:grid; grid-template-columns: 1fr; gap: 12px; }
.msiemTableWrap { border: 1px solid rgba(0,0,0,.12); border-radius: 12px; overflow:hidden; background: rgba(255,255,255,.92); }
.msiemTableTop { display:flex; gap: 10px; align-items:center; justify-content: space-between; flex-wrap: wrap; padding: 10px 12px; border-bottom: 1px solid rgba(0,0,0,.08); }
.msiemTable { width: 100%; border-collapse: collapse; font-size: .9rem; }
.msiemTable th, .msiemTable td { padding: 8px 10px; border-bottom: 1px solid rgba(0,0,0,.06); vertical-align: top; }
.msiemTable th { text-align:left; opacity: .8; font-weight: 900; background: rgba(0,0,0,.02); }
.msiemMuted { opacity: .72; }
.msiemMono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: .85rem; }

.msiemAlerts { display:grid; gap: 10px; }
.msiemAlert { border: 1px solid rgba(0,0,0,.12); border-radius: 12px; background: rgba(255,255,255,.92); padding: 10px 12px; }
.msiemAlertTop { display:flex; gap: 10px; align-items:flex-start; justify-content: space-between; }
.msiemAlertTitle { font-weight: 900; }
.msiemAlertMeta { margin-top: 6px; font-size: .88rem; opacity: .78; }
.msiemSeverityHigh { background: rgba(180,0,0,.08); border-color: rgba(180,0,0,.18); }
.msiemSeverityMid  { background: rgba(170,120,0,.08); border-color: rgba(170,120,0,.18); }

.msiemScoreList { display:grid; gap: 10px; }
.msiemScoreRow { border: 1px solid rgba(0,0,0,.12); border-radius: 12px; background: rgba(255,255,255,.92); padding: 10px 12px; }
.msiemScoreTop { display:flex; gap: 10px; align-items:center; justify-content: space-between; flex-wrap: wrap; }
.msiemScoreUser { font-weight: 900; }
.msiemScoreVal { font-weight: 900; }
.msiemScoreTrack { height: 10px; border-radius: 999px; background: rgba(0,0,0,.08); overflow:hidden; margin-top: 8px; }
.msiemScoreFill { height: 100%; background: rgba(0,0,0,.45); width: 0%; }

.msiemLegend {text-align: left; margin-top: 12px; border-top: 1px dashed rgba(0,0,0,.18); padding-top: 10px; }
.msiemLegend summary { cursor: pointer; font-weight: 900; }
.msiemLegend ul { margin: 10px 0 0 18px; }
.msiemLegend li { margin: 6px 0; }


`;

/* ----------------------------- Component ------------------------------- */

export default function MiniSIEMLab({
  title = "Mini‑SIEM (Simulation): Logs → Korrelation → Alerts",  
}) {
  const [scenarioKey, setScenarioKey] = useState("webshop");
  const [running, setRunning] = useState(false);

  // Geschwindigkeit: wie schnell Simulation-Zeit voranschreitet
  const [speed, setSpeed] = useState(3); // 1..5
  // Rauschen: wie viele „normale“ Events zusätzlich kommen
  const [noise, setNoise] = useState(55); // 0..100

  const [showRaw, setShowRaw] = useState(false);
  const [search, setSearch] = useState("");

  const [rulesEnabled, setRulesEnabled] = useState(DEFAULT_RULES);

  // False‑Positive‑Schalter (nur Bank)
  const [bankBenignDemo, setBankBenignDemo] = useState(false);

  const rngRef = useRef(mulberry32(123456));
  const simTsRef = useRef(Date.now());
  const dedupeRef = useRef(new Map());

  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const maxEvents = 220;
  const maxAlerts = 80;

  // Reset bei Szenariowechsel
  useEffect(() => {
    setRunning(false);
    rngRef.current = mulberry32(scenarioKey === "webshop" ? 123456 : 777777);
    simTsRef.current = Date.now();
    dedupeRef.current = new Map();
    setEvents([]);
    setAlerts([]);
    setSearch("");
    setRulesEnabled(DEFAULT_RULES);
    setBankBenignDemo(false);
  }, [scenarioKey]);

  const scenario = useMemo(
    () => SCENARIOS.find((s) => s.key === scenarioKey) || SCENARIOS[0],
    [scenarioKey]
  );

  const defs = useMemo(() => ruleDefs(scenarioKey), [scenarioKey]);

  const stats = useMemo(() => {
    const last1m = within(events, simTsRef.current, 60 * 1000);
    const blocks = last1m.filter((e) => e.type === "WAF_BLOCK").length;
    const loginsFail = last1m.filter((e) => e.type === "LOGIN_FAIL").length;
    const loginsOk = last1m.filter((e) => e.type === "LOGIN_SUCCESS").length;

    const high = alerts.filter((a) => a.severity === "hoch").length;
    const mid = alerts.filter((a) => a.severity === "mittel").length;

    return { blocks, loginsFail, loginsOk, high, mid, last1mCount: last1m.length };
  }, [events, alerts]);

  const filteredEvents = useMemo(() => {
    if (!search.trim()) return events;
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      const blob = `${e.source} ${e.type} ${e.user || ""} ${e.ip || ""} ${e.geo || ""} ${
        e.outcome || ""
      } ${e.msg || ""} ${e.raw || ""}`;
      return blob.toLowerCase().includes(q);
    });
  }, [events, search]);

  const fraudScores = useMemo(() => {
    if (scenarioKey !== "bank") return [];
    return computeFraudScoresForBank(events, simTsRef.current);
  }, [events, scenarioKey]);

  const pushEvent = (newEvent) => {
    setEvents((prev) => {
      const next = [newEvent, ...prev].slice(0, maxEvents);

      const newAlerts = runCorrelation({
        newEvent,
        allEvents: next,
        scenarioKey,
        rulesEnabled,
        dedupeRef,
      });

      if (newAlerts.length) {
        setAlerts((prevA) => [...newAlerts, ...prevA].slice(0, maxAlerts));
      }

      return next;
    });
  };

  const tick = () => {
    const rng = rngRef.current;

    const stepSec = [15, 30, 60, 120, 180][clamp(speed - 1, 0, 4)];
    simTsRef.current += stepSec * 1000;

    pushEvent(genNormalEvent({ scenarioKey, rng, nowTs: simTsRef.current }));

    const extra = Math.floor((noise / 100) * (speed + 1)); // 0..6 grob
    for (let i = 0; i < extra; i++) {
      const dt = 200 + Math.floor(rng() * 1200);
      pushEvent(genNormalEvent({ scenarioKey, rng, nowTs: simTsRef.current + dt }));
    }
  };

  useEffect(() => {
    if (!running) return;
    const intervalMs = 650;
    const t = setInterval(tick, intervalMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, speed, noise, scenarioKey, rulesEnabled]);

  const inject = () => {
    const seq = injectPattern({
      scenarioKey,
      nowTs: simTsRef.current + 2000,
      benign: scenarioKey === "bank" ? bankBenignDemo : false,
    });

    seq.forEach((e, idx) => {
      setTimeout(() => pushEvent(e), idx * 15);
    });
  };

  const clearLogs = () => setEvents([]);
  const clearAlerts = () => setAlerts([]);
  const resetDedupe = () => (dedupeRef.current = new Map());

  return (
    <figure className="msiem">
      <style>{css}</style>

      <div className="msiemCard">
        <div className="msiemHeader">
          <div>
            <h3 className="msiemTitle">{title}</h3>
           
          </div>

          <div className="msiemRow">
            <select
              className="msiemSelect"
              value={scenarioKey}
              onChange={(e) => setScenarioKey(e.target.value)}
              aria-label="Szenario wählen"
            >
              {SCENARIOS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              className={`msiemBtn ${running ? "" : "msiemBtnPrimary"}`}
              onClick={() => setRunning((v) => !v)}
            >
              {running ? "Stop" : "Start"}
            </button>

            <button type="button" className="msiemBtn" onClick={inject}>
              Muster einspeisen
            </button>

            {scenarioKey === "bank" && (
              <label className="msiemMuted" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={bankBenignDemo}
                  onChange={(e) => setBankBenignDemo(e.target.checked)}
                />
                False‑Positive‑Demo (legitimer Fall)
              </label>
            )}

            <button type="button" className="msiemBtn" onClick={clearLogs}>
              Logs leeren
            </button>

            <button type="button" className="msiemBtn" onClick={clearAlerts}>
              Alerts leeren
            </button>

            <button
              type="button"
              className="msiemBtn"
              onClick={resetDedupe}
              title="Erlaubt Regeln wieder schneller auszulösen"
            >
              Cooldown reset
            </button>
          </div>
        </div>

        <div className="msiemStats">
          <div className="msiemStat">
            <div className="msiemStatTitle">Simulation‑Zeit</div>
            <div className="msiemStatValue">{fmtTime(simTsRef.current)}</div>
            <div className="msiemMuted">Szenario: {scenario.name}</div>
          </div>

          <div className="msiemStat">
            <div className="msiemStatTitle">Letzte „Minute“ (simuliert)</div>
            <div className="msiemStatValue">{stats.last1mCount} Events</div>
            <div className="msiemMuted">
              Logins ok: {stats.loginsOk} • Logins fail: {stats.loginsFail} • WAF blocks: {stats.blocks}
            </div>
          </div>

          <div className="msiemStat">
            <div className="msiemStatTitle">Alerts</div>
            <div className="msiemStatValue">
              <span className="msiemTag">hoch: {stats.high}</span>{" "}
              <span className="msiemTag">mittel: {stats.mid}</span>
            </div>
            <div className="msiemMuted">Erzeugt durch einfache Korrelation‑Regeln</div>
          </div>

          <div className="msiemStat">
            <div className="msiemStatTitle">Kontrollen</div>
            <div className="msiemMuted" style={{ marginTop: 6 }}>
              Speed: {speed}/5 • Noise: {noise}/100 • Rohlog: {showRaw ? "an" : "aus"}
            </div>

            <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
              <label className="msiemMuted">
                Speed (Simulation)
                <input
                  className="msiemInput"
                  style={{ width: "100%", marginTop: 6 }}
                  type="range"
                  min="1"
                  max="5"
                  value={speed}
                  onChange={(e) => setSpeed(Number(e.target.value))}
                />
              </label>

              <label className="msiemMuted">
                Noise (Hintergrund‑Events)
                <input
                  className="msiemInput"
                  style={{ width: "100%", marginTop: 6 }}
                  type="range"
                  min="0"
                  max="100"
                  value={noise}
                  onChange={(e) => setNoise(Number(e.target.value))}
                />
              </label>

              <label className="msiemMuted" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={showRaw}
                  onChange={(e) => setShowRaw(e.target.checked)}
                />
                Rohlog anzeigen (zusätzlich)
              </label>
            </div>
          </div>
        </div>

        <div className="msiemGrid">
          {/* Left: Logs */}
          <div className="msiemPanel">
            <div className="msiemPanelTitle">Log‑Stream (normalisierte Events)</div>

            <div className="msiemTableWrap">
              <div className="msiemTableTop">
                <div className="msiemRow">
                  <span className="msiemTag">Quelle: {scenario.sources.join(", ")}</span>
                  <span className="msiemTag">Nutzer: {scenario.users.join(", ")}</span>
                  <span className="msiemTag">Geo normal: DE</span>
                </div>

                <input
                  className="msiemInput"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filtern (z. B. LOGIN_FAIL, cust_anna, TRANSFER_CONFIRM)"
                  aria-label="Logs filtern"
                  style={{ minWidth: 320 }}
                />
              </div>

              <table className="msiemTable">
                <thead>
                  <tr>
                    <th style={{ width: 78 }}>Zeit</th>
                    <th style={{ width: 70 }}>Quelle</th>
                    <th style={{ width: 160 }}>Typ</th>
                    <th style={{ width: 120 }}>User</th>
                    <th style={{ width: 110 }}>IP</th>
                    <th style={{ width: 80 }}>Geo</th>
                    <th>Info</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="msiemMuted">
                        Keine Events (Start drücken oder Filter löschen).
                      </td>
                    </tr>
                  ) : (
                    filteredEvents.map((e) => (
                      <tr key={e.id}>
                        <td className="msiemMono">{fmtTime(e.ts)}</td>
                        <td>{e.source}</td>
                        <td className="msiemMono">{e.type}</td>
                        <td className="msiemMono">{e.user || "—"}</td>
                        <td className="msiemMono">{e.ip || "—"}</td>
                        <td className="msiemMono">{e.geo || "—"}</td>
                        <td>
                          <div>{e.msg || "—"}</div>
                          {showRaw && (
                            <div className="msiemMono msiemMuted" style={{ marginTop: 6 }}>
                              {e.raw}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="msiemSub" style={{ marginTop: 10 }}>
              Lernidee: Einzelne Events sind oft „normal“. Erst die Kombination (Korrelation) macht ein Muster.
              <br />
              Hinweis: Normale Geo‑Logs bleiben in DE. Für Geo‑Anomalien nutze „Muster einspeisen“.
            </div>
          </div>

          {/* Right: Rules + Alerts + Fraud Score */}
          <div className="msiemSplit">
            <div className="msiemPanel">
              <div className="msiemPanelTitle">Korrelation‑Regeln</div>
              <div className="msiemRules">
                {defs.map((r) => (
                  <div className="msiemRule" key={r.key}>
                    <div className="msiemRuleLeft">
                      <div className="msiemRuleName">{r.name}</div>
                      <div className="msiemRuleHelp">{r.explain}</div>
                    </div>
                    <div className="msiemRuleToggle">
                      <button
                        type="button"
                        className={`msiemBtn ${rulesEnabled[r.key] ? "msiemBtnPrimary" : ""}`}
                        onClick={() =>
                          setRulesEnabled((prev) => ({ ...prev, [r.key]: !prev[r.key] }))
                        }
                        aria-pressed={!!rulesEnabled[r.key]}
                      >
                        {rulesEnabled[r.key] ? "aktiv" : "aus"}
                      </button>
                      <div className="msiemSmall">Schwere: {r.severity}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {scenarioKey === "bank" && (
              <div className="msiemPanel">
                <div className="msiemPanelTitle">Fraud Score (0–100) – didaktisch</div>

                {fraudScores.length === 0 ? (
                  <div className="msiemSub">
                    Noch keine passenden Events. Starte die Simulation oder nutze „Muster einspeisen“.
                  </div>
                ) : (
                  <div className="msiemScoreList">
                    {fraudScores.map((x) => (
                      <div className="msiemScoreRow" key={x.user}>
                        <div className="msiemScoreTop">
                          <div className="msiemScoreUser">{x.user}</div>
                          <div className="msiemScoreVal">{x.score}</div>
                        </div>
                        <div className="msiemScoreTrack" aria-hidden="true">
                          <div className="msiemScoreFill" style={{ width: `${x.score}%` }} />
                        </div>
                        <div className="msiemMuted" style={{ marginTop: 6 }}>
                          {x.reasons.length ? x.reasons.join(" • ") : "Keine auffälligen Signale im Zeitfenster"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="msiemSub" style={{ marginTop: 10 }}>
                  Idee: Ein Score ist keine „Wahrheit“, sondern hilft beim Priorisieren.
                  False‑Positive‑Demo zeigt: ein Alarm kann entstehen, obwohl es legitime Gründe geben kann.
                </div>
              </div>
            )}

            <div className="msiemPanel">
              <div className="msiemPanelTitle">Alerts (aus Korrelation)</div>

              {alerts.length === 0 ? (
                <div className="msiemSub">
                  Noch keine Alerts. Starte die Simulation oder klicke „Muster einspeisen“.
                </div>
              ) : (
                <div className="msiemAlerts">
                  {alerts.map((a) => (
                    <div
                      key={a.id}
                      className={`msiemAlert ${
                        a.severity === "hoch" ? "msiemSeverityHigh" : "msiemSeverityMid"
                      }`}
                    >
                      <div className="msiemAlertTop">
                        <div>
                          <div className="msiemAlertTitle">{a.title}</div>
                          <div className="msiemAlertMeta">
                            {fmtTime(a.ts)} • Regel: <span className="msiemMono">{a.ruleKey}</span> • Schwere:{" "}
                            <strong>{a.severity}</strong>
                          </div>
                        </div>
                        <span className="msiemTag">{a.ruleName}</span>
                      </div>
                      <div className="msiemAlertMeta">{a.details}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="msiemSub" style={{ marginTop: 10 }}>
                Echte SIEMs können zusätzlich: Parser, Dashboards, Threat Intel, Tickets/Workflows, Response‑Automatisierung –
                hier ist es bewusst klein.
              </div>
            </div>
          </div>
        </div>

        {/* Mini-Legende */}
        <div className="msiemLegend">
          <details>
            <summary>Mini‑Legende (Begriffe)</summary>
            <ul>
              <li><strong>WAF</strong> (Web Application Firewall): Filtert Web‑Anfragen und blockiert verdächtige Muster.</li>
              <li><strong>MFA</strong> (Multi‑Factor Authentication): Zusätzlicher Faktor beim Login (z. B. App‑Bestätigung).</li>
              <li><strong>Geo</strong>: Grobe Region/Land, abgeleitet aus der IP (hier normal DE).</li>
              <li><strong>Event</strong>: Normalisierter Log‑Eintrag (Zeit, Quelle, Typ, User, IP …).</li>
              <li><strong>Korrelation</strong>: Regel verknüpft mehrere Events zu einem Muster.</li>
              <li><strong>Alert</strong>: Hinweis „bitte prüfen“ – kann auch ein <em>false positive</em> sein.</li>
              <li><strong>Fraud Score</strong>: Vereinfachte Priorisierung (mehr Signale → höherer Score), keine Garantie.</li>
            </ul>
          </details>
        </div>

       
      </div>
    </figure>
  );
}